import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  getClientSettings,
  saveClientSettings,
  smartMarketplaceErrorMessage,
  type ClientSettings,
  type ClientSettingsGoal,
} from '@/api/smartMarketplace';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import {
  ACOS_PERCENTS,
  CHANGE_PCTS,
  PLACEMENT_PCTS,
  ROAS_VALUES,
  clientSettingsCanSave,
  formFromSettings,
  placementRangeInvalid,
  selectValues,
  toClientSettings,
  type ClientSettingsFormState,
} from '@/components/sapyonAgent/clientSettings';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-body-sm font-medium text-foreground-muted">{label}</span>
      {children}
    </label>
  );
}

function formatOption(value: number, suffix: string): string {
  return `${String(value)}${suffix}`;
}

function NumberSelect({
  label,
  value,
  presets,
  emptyLabel,
  suffix,
  floor,
  onChange,
}: {
  label: string;
  value: string;
  presets: readonly number[];
  emptyLabel: string;
  suffix: string;
  floor?: string;
  onChange: (value: string) => void;
}) {
  const floorValue = floor == null || floor === '' ? null : Number(floor);
  return (
    <Field label={label}>
      <Select className="w-full" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{emptyLabel}</option>
        {selectValues(presets, value).map((item) => (
          <option
            key={item}
            value={String(item)}
            disabled={floorValue != null && Number.isFinite(floorValue) && item < floorValue}
          >
            {formatOption(item, suffix)}
          </option>
        ))}
      </Select>
    </Field>
  );
}

export function ClientSettingsForm({ profileId }: { profileId: string }) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['smart-marketplace-client-settings', profileId] as const;
  const [draft, setDraft] = useState<ClientSettingsFormState | null>(null);

  const settingsQuery = useQuery({
    queryKey,
    queryFn: () => getClientSettings(profileId),
    enabled: Boolean(profileId),
    staleTime: Infinity,
  });

  const form = draft ?? formFromSettings(settingsQuery.data?.settings);
  const rangeInvalid = placementRangeInvalid(form);
  const canSave = clientSettingsCanSave(form);

  const save = useMutation({
    mutationFn: (settings: ClientSettings) => saveClientSettings(profileId, settings),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKey, saved);
      setDraft(null);
      showToast('Client settings saved.');
    },
    onError: (error) => {
      showToast(smartMarketplaceErrorMessage(error, 'Could not save client settings.'), 'error');
    },
  });

  function update(patch: Partial<ClientSettingsFormState>) {
    setDraft({ ...form, ...patch });
  }

  function onGoal(goal: ClientSettingsGoal | '') {
    update({
      goal,
      targetAcosPercent: goal === 'target_acos' ? form.targetAcosPercent : '',
      targetRoas: goal === 'target_roas' ? form.targetRoas : '',
    });
  }

  const loadError = settingsQuery.error
    ? smartMarketplaceErrorMessage(settingsQuery.error, 'Could not load client settings.')
    : null;
  const brandName = settingsQuery.data?.brand_name?.trim();
  const showFields = settingsQuery.isSuccess || settingsQuery.isError;

  return (
    <form
      className="space-y-4 rounded-xl border border-border bg-surface p-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSave || save.isPending) return;
        save.mutate(toClientSettings(form));
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-body-md font-semibold text-brand-ink">Client settings</h2>
          {brandName ? <p className="mt-1 text-body-sm text-foreground">{brandName}</p> : null}
          <p className="mt-1 max-w-2xl text-body-sm text-foreground-muted">
            These values are what the brand agent uses on the next run.
          </p>
        </div>
        <Button type="submit" disabled={!showFields || !canSave || save.isPending}>
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          Save
        </Button>
      </div>

      {loadError ? (
        <p className="rounded-lg border border-error/20 bg-error-tint px-3 py-2 text-body-sm text-error-ink">
          {loadError}
        </p>
      ) : null}

      {settingsQuery.isLoading ? (
        <p className="text-body-sm text-foreground-muted">Loading client settings</p>
      ) : null}

      {showFields ? (
        <>
          <fieldset className="space-y-3">
            <legend className="text-body-sm font-semibold text-brand-ink">Objective</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Goal">
                <Select
                  className="w-full"
                  value={form.goal}
                  onChange={(event) => onGoal(event.target.value as ClientSettingsGoal | '')}
                >
                  <option value="">Select a goal</option>
                  <option value="target_acos">Target ACOS</option>
                  <option value="target_roas">Target ROAS</option>
                  <option value="efficiency">Efficiency</option>
                </Select>
              </Field>
              {form.goal === 'target_acos' ? (
                <NumberSelect
                  label="Target ACOS"
                  value={form.targetAcosPercent}
                  presets={ACOS_PERCENTS}
                  emptyLabel="Select a target"
                  suffix="%"
                  onChange={(targetAcosPercent) => update({ targetAcosPercent })}
                />
              ) : null}
              {form.goal === 'target_roas' ? (
                <NumberSelect
                  label="Target ROAS"
                  value={form.targetRoas}
                  presets={ROAS_VALUES}
                  emptyLabel="Select a target"
                  suffix=""
                  onChange={(targetRoas) => update({ targetRoas })}
                />
              ) : null}
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-body-sm font-semibold text-brand-ink">Limits</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <NumberSelect
                label="Bid change limit"
                value={form.maxBid}
                presets={CHANGE_PCTS}
                emptyLabel="No limit"
                suffix="%"
                onChange={(maxBid) => update({ maxBid })}
              />
              <NumberSelect
                label="Budget change limit"
                value={form.maxBudget}
                presets={CHANGE_PCTS}
                emptyLabel="No limit"
                suffix="%"
                onChange={(maxBudget) => update({ maxBudget })}
              />
              <NumberSelect
                label="Minimum placement adjustment"
                value={form.minPlacement}
                presets={PLACEMENT_PCTS}
                emptyLabel="No limit"
                suffix="%"
                onChange={(minPlacement) => update({ minPlacement })}
              />
              <NumberSelect
                label="Maximum placement adjustment"
                value={form.maxPlacement}
                presets={PLACEMENT_PCTS}
                emptyLabel="No limit"
                suffix="%"
                floor={form.minPlacement}
                onChange={(maxPlacement) => update({ maxPlacement })}
              />
            </div>
            {rangeInvalid ? (
              <p className="text-body-sm text-error-ink">
                Minimum placement adjustment cannot exceed the maximum.
              </p>
            ) : null}
          </fieldset>
        </>
      ) : null}
    </form>
  );
}
